import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import Clarity from "@microsoft/clarity";
import ReactGA from "react-ga4";
import { queryClient } from "./apis/common/queryClient";
import AuthProvider from "./providers/authProvider/authProvider.tsx";
import SnackbarProvider from "./providers/snackbar-provider/snackbarProvider";
import DialogProvider from "./providers/dialogProvider/dialogProvider";
import ThemeProvider from "./theme";
import { router } from "./router";
import { CLARITY_PROJECT_MAP } from "./utils/constants";
import { isRelease } from "./utils/enviroment.helper";

const isLocal = window.location.hostname === "localhost";

// Sentry
const maskTokenInUrl = (url: string) =>
  url.replace(/([?&]token=)[^&#]+/gi, "$1[Filtered]");

if (!isLocal) {
  Sentry.init({
    dsn: "https://d3d31204bd2bb4f5a1dfa16571dca059@o4510033186127873.ingest.us.sentry.io/4510939731722240",
    environment: import.meta.env.MODE,
    sendDefaultPii: true,
    // nani club OIDC 回程的 ?token= 為 jwt，不進 Sentry
    beforeSend: (event) => {
      if (event.request?.url) {
        event.request.url = maskTokenInUrl(event.request.url);
      }
      return event;
    },
  });
}

// Clarity
const clarityProjectId = CLARITY_PROJECT_MAP[window.location.hostname];

if (isRelease && clarityProjectId) {
  Clarity.init(clarityProjectId);
}

// Google Analytics
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

if (GA_MEASUREMENT_ID) {
  ReactGA.initialize(GA_MEASUREMENT_ID);
}

const root = createRoot(document.getElementById("root")!, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <SnackbarProvider>
            <DialogProvider>
              <RouterProvider router={router} />
            </DialogProvider>
          </SnackbarProvider>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>
);
